import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, Typography, Container, CircularProgress, Paper, 
  Alert, Button, Divider, Collapse, IconButton 
} from "@mui/material";
import { ExpandMore, ExpandLess, DeleteOutline } from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";

import PublicInfoCard from "./components/PublicInfoCard";
import PublicRoomsCard from "./components/PublicRoomsCard";
import PublicContactsSection from "./components/PublicContactsSection";
import { LanguageSwitcher, LoadingDots } from "../../../components/general";


const AdmissionFormPublic = () => {
  // Get token from URL parameters
  const { token } = useParams();
  
  // Translation hooks for internationalization
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  // Main state variables
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Error state for form validation
  const [p1Errors, setP1Errors] = useState({});
  const [p2Errors, setP2Errors] = useState({});
  const [childrenErrors, setChildrenErrors] = useState([]);
  const [childGeneralError, setChildGeneralError] = useState(null);

  // Track if email has been entered/checked
  const [p1EmailChecked, setP1EmailChecked] = useState(false);
  const [p2EmailChecked, setP2EmailChecked] = useState(false);
  
  // Success messages when parent exists
  const [p1ExistsMessage, setP1ExistsMessage] = useState(null);
  const [p2ExistsMessage, setP2ExistsMessage] = useState(null);

  // Metadata from backend (rooms, nationalities, genders, etc.)
  const [metadata, setMetadata] = useState({
    rooms: [],
    nationalities: [],
    genders: [],
    countries: [],
    contactTypes: []
  });

  // Parent 1 information state
  const [parent1, setParent1] = useState({ 
    isExpanded: true, 
    isExisting: false,
    p1_first_name: "", 
    p1_last_name: "", 
    p1_email: "", 
    p1_phone: "", 
    p1_address: "", 
    p1_gender: "", 
    p1_country: "" 
  });
  
  // Parent 2 information state
  const [parent2, setParent2] = useState({ 
    isExpanded: false, 
    isExisting: false,
    p2_first_name: "", 
    p2_last_name: "", 
    p2_email: "", 
    p2_phone: "", 
    p2_address: "", 
    p2_gender: "", 
    p2_country: "" 
  });

  // Children information state (array of child objects)
  const [children, setChildren] = useState([
    { 
      id: Date.now(), 
      isExpanded: true,
      FirstName: "", 
      LastName: "", 
      BirthDate: "", 
      Gender: "",
      Notes: "", 
      Medications: "", 
      DoctorName: "", 
      DoctorPhone: "", 
      Allergies: "",
      AppartmentHouse: "", 
      ZipCode: "", 
      City: "", 
      State: "", 
      Street: "",
      Classes: [] 
    }
  ]);

  // Emergency contacts state
  const [emergencyContacts, setEmergencyContacts] = useState([]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch validation data first
        const validateRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission/validate/${token}/`);
        
        // Store validation response
        setData(validateRes.data);
        
        // If link is not valid, set error and stop here
        if (!validateRes.data.valid) {
          setError(validateRes.data.error);
          setLoading(false);
          return;
        }
        
        // If valid, fetch metadata
        const metadataRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission/metadata/${token}/`);
        
        // Store metadata if successful
        if (metadataRes.data.success) {
          setMetadata({
            rooms: metadataRes.data.rooms || [],
            nationalities: metadataRes.data.nationalities || [],
            genders: metadataRes.data.genders || [],
            countries: metadataRes.data.countries || [],
            contactTypes: metadataRes.data.contact_types || []
          });
        } else {
          // If metadata fetch fails with error message, translate if needed
          const metadataError = metadataRes.data.error;
          if (metadataError === "This admission link has been closed") {
            setError(isAr ? "تم إغلاق رابط القبول هذا" : metadataError);
          } else {
            setError(metadataError || (isAr ? "خطأ في تحميل البيانات" : "Error loading data"));
          }
        }
      } catch (err) {
        // Handle 404 errors specifically
        if (err.response && err.response.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        
        // Handle other errors with error message from backend
        if (err.response && err.response.data) {
          if (err.response.data.error) {
            setError(err.response.data.error);
          } else if (err.response.data.valid === false) {
            setData(err.response.data);
            setError(err.response.data.error);
          } else {
            setError(isAr ? "خطأ في الاتصال بالخادم." : "Connection error.");
          }
        } else {
          // Generic connection error
          setError(isAr ? "خطأ في الاتصال بالخادم." : "Connection error.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, isAr]);

  // Check parent email
  const checkParentEmail = async (email, parentNumber) => {
    if (!email?.trim() || !email.includes('@')) {
      // Clear existing data if email is invalid
      if (parentNumber === 1) {
        setParent1(prev => ({ 
          ...prev, 
          isExisting: false,
          p1_first_name: "",
          p1_last_name: "",
          p1_phone: "",
          p1_address: "",
          p1_gender: "",
          p1_country: "",
        }));
        setP1EmailChecked(false);
        setP1ExistsMessage(null);
      } else {
        setParent2(prev => ({ 
          ...prev, 
          isExisting: false,
          p2_first_name: "",
          p2_last_name: "",
          p2_phone: "",
          p2_address: "",
          p2_gender: "",
          p2_country: "",
        }));
        setP2EmailChecked(false);
        setP2ExistsMessage(null);
      }
      return;
    }
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission/check-parent/`,
        { params: { email: email.toLowerCase().trim() } }
      );

      if (response.data.exists && response.data.parent) {
        const pData = response.data.parent;
        const updateData = {
          isExisting: true,
          [`p${parentNumber}_first_name`]: pData.first_name || "",
          [`p${parentNumber}_last_name`]: pData.last_name || "",
          [`p${parentNumber}_phone`]: pData.phone || "",
          [`p${parentNumber}_address`]: pData.address || "",
          [`p${parentNumber}_gender`]: pData.gender_id || "",
          [`p${parentNumber}_country`]: pData.country || "",
        };

        if (parentNumber === 1) {
          setParent1(prev => ({ ...prev, ...updateData }));
          setP1EmailChecked(true);
          setP1ExistsMessage(isAr ? "✓ تم العثور على بياناتك بنجاح" : "✓ Your data has been found successfully");
        } else {
          setParent2(prev => ({ ...prev, ...updateData }));
          setP2EmailChecked(true);
          setP2ExistsMessage(isAr ? "✓ تم العثور على بياناتك بنجاح" : "✓ Your data has been found successfully");
        }
      } else {
        // Email not found - new parent
        if (parentNumber === 1) {
          setParent1(prev => ({ ...prev, isExisting: false }));
          setP1EmailChecked(true);
          setP1ExistsMessage(null);
        } else {
          setParent2(prev => ({ ...prev, isExisting: false }));
          setP2EmailChecked(true);
          setP2ExistsMessage(null);
        }
      }
    } catch (err) {
      console.error("Error checking parent email:", err);
      if (parentNumber === 1) {
        setP1EmailChecked(true);
      } else {
        setP2EmailChecked(true);
      }
    }
  };


  // Handle email change - allow editing even after check
  const handleEmailChange = (email, parentNumber) => {
    if (parentNumber === 1) {
      setParent1(prev => ({ 
        ...prev, 
        p1_email: email,
        isExisting: false 
      }));
      setP1EmailChecked(false);
      setP1ExistsMessage(null);
      setP1Errors(prev => ({ ...prev, p1_email: null }));
    } else {
      setParent2(prev => ({ 
        ...prev, 
        p2_email: email,
        isExisting: false 
      }));
      setP2EmailChecked(false);
      setP2ExistsMessage(null);
      setP2Errors(prev => ({ ...prev, p2_email: null }));
    }
  };


  // Debounce check for Parent 1 Email
  useEffect(() => {
    const email = parent1.p1_email;
    
    if (!email?.trim() || !email.includes('@')) return;

    const timer = setTimeout(() => {
      checkParentEmail(email, 1);
    }, 700); 

    return () => clearTimeout(timer); 
  }, [parent1.p1_email]);

  // Debounce check for Parent 2 Email
  useEffect(() => {
    const email = parent2.p2_email;
    
    if (!email?.trim() || !email.includes('@')) return;

    const timer = setTimeout(() => {
      checkParentEmail(email, 2);
    }, 700);

    return () => clearTimeout(timer);
  }, [parent2.p2_email]);


  // Scroll to first error field in the form
  const scrollToFirstError = () => {
    setTimeout(() => {
      // Query for any element with error styling or aria-invalid attribute
      const errorElement = document.querySelector(
        ".Mui-error, [class*=\"error\"], [aria-invalid=\"true\"], .MuiFormHelperText-root.Mui-error"
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  };

  // Frontend validation before submission
  const validateFrontEnd = () => {
    let isValid = true;
    const p1Errs = {};
    const p2Errs = {};
    const cErrs = [];

    // Helper function to check if a field is empty
    const isFieldEmpty = (val) => !val || (typeof val === 'string' && val.trim() === "");

    // PARENT 1 VALIDATION
    // For existing parents, we only need to ensure the email is provided 
    // since other data is fetched automatically and usually locked
    // For new parents, all profile fields are mandatory
    if (parent1.isExisting) {
      if (isFieldEmpty(parent1.p1_email)) {
        p1Errs.p1_email = isAr ? "هذا الحقل مطلوب" : "Required";
        isValid = false;
      }
    } else {
      const fields1 = [
        'p1_first_name', 
        'p1_last_name', 
        'p1_email', 
        'p1_phone', 
        'p1_address', 
        'p1_gender', 
        'p1_country'
      ];
      
      fields1.forEach(f => {
        if (isFieldEmpty(parent1[f])) {
          p1Errs[f] = isAr ? "هذا الحقل مطلوب" : "Required";
          isValid = false;
        }
      });
    }

    // PARENT 2 VALIDATION - COMPLETELY OPTIONAL
    // Parent 2 is optional BUT if user enters ANY data, they MUST provide email + name
    // Phone, Address, Gender, Country are ALWAYS optional for Parent 2
    const hasP2Email = !isFieldEmpty(parent2.p2_email);
    const hasP2FirstName = !isFieldEmpty(parent2.p2_first_name);
    const hasP2LastName = !isFieldEmpty(parent2.p2_last_name);
    const hasP2Phone = !isFieldEmpty(parent2.p2_phone);
    const hasP2Address = !isFieldEmpty(parent2.p2_address);
    const hasP2Gender = !isFieldEmpty(parent2.p2_gender);
    const hasP2Country = !isFieldEmpty(parent2.p2_country);
    
    // Check if user entered ANY Parent 2 data at all
    const hasAnyP2Data = hasP2Email || hasP2FirstName || hasP2LastName || 
                         hasP2Phone || hasP2Address || hasP2Gender || hasP2Country;

    // If user entered ANY Parent 2 data, validate ONLY email + name
    // All other fields (phone, address, gender, country) are OPTIONAL
    if (hasAnyP2Data) {
      // Email is REQUIRED if any P2 data exists
      if (!hasP2Email) {
        p2Errs.p2_email = isAr ? "هذا الحقل مطلوب عند إدخال بيانات ولي الأمر الثاني" : "Required when adding Parent 2";
        isValid = false;
      }
      // First Name is REQUIRED if any P2 data exists
      if (!hasP2FirstName) {
        p2Errs.p2_first_name = isAr ? "هذا الحقل مطلوب عند إدخال بيانات ولي الأمر الثاني" : "Required when adding Parent 2";
        isValid = false;
      }
      // Last Name is REQUIRED if any P2 data exists
      if (!hasP2LastName) {
        p2Errs.p2_last_name = isAr ? "هذا الحقل مطلوب عند إدخال بيانات ولي الأمر الثاني" : "Required when adding Parent 2";
        isValid = false;
      }
      
      // Phone, Address, Gender, Country are ALWAYS OPTIONAL for Parent 2
      // No validation needed for these fields
    }
    // If NO Parent 2 data at all, skip validation - this is perfectly OK!

    // Validate each child's required fields
    children.forEach((child, index) => {
      const currentChildErr = {};
      
      // Text fields validation
      if (isFieldEmpty(child.FirstName)) currentChildErr.FirstName = [isAr ? "هذا الحقل مطلوب" : "Required"];
      if (isFieldEmpty(child.LastName)) currentChildErr.LastName = [isAr ? "هذا الحقل مطلوب" : "Required"];
      if (isFieldEmpty(child.BirthDate)) currentChildErr.BirthDate = [isAr ? "هذا الحقل مطلوب" : "Required"];
      
      // Select fields validation
      if (isFieldEmpty(child.Gender)) currentChildErr.Gender = [isAr ? "هذا الحقل مطلوب" : "Required"];
      
      // Classes selection validation
      if (!child.Classes || child.Classes.length === 0) {
        currentChildErr.Classes = [isAr ? "يجب اختيار فصل واحد على الأقل" : "Select at least one class"];
      }
      
      cErrs[index] = currentChildErr;
      if (Object.keys(currentChildErr).length > 0) isValid = false;
    });

    // Update error states
    setP1Errors(p1Errs);
    setP2Errors(p2Errs);
    setChildrenErrors(cErrs);

    // If validation fails, expand sections with errors and scroll to first error
    if (!isValid) {
      // Expand parent sections if they have errors
      if (Object.keys(p1Errs).length > 0) {
        setParent1(p => ({ ...p, isExpanded: true }));
      }
      if (Object.keys(p2Errs).length > 0) {
        setParent2(p => ({ ...p, isExpanded: true }));
      }
      
      // Expand child sections if they have errors
      const newChildren = [...children];
      children.forEach((child, idx) => {
        if (cErrs[idx] && Object.keys(cErrs[idx]).length > 0) {
          newChildren[idx].isExpanded = true;
        }
      });
      setChildren(newChildren);
      
      // Scroll to first error after state updates
      scrollToFirstError();
    }
    
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Clear previous errors
    setError(null);
    setP1Errors({});
    setP2Errors({});
    setChildrenErrors([]);
    setChildGeneralError(null);

    // Validate form before submitting
    if (!validateFrontEnd()) {
      setError(isAr ? "يرجى التأكد من ملء جميع الحقول المطلوبة." : "Please ensure all required fields are filled.");
      return;
    }

    setSubmitting(true);
    
    // Prepare payload for API
    const payload = {
      link_token: token,
      ...parent1,
      ...parent2,
      children: children.map(child => ({
        ...child,
        // Convert Classes to array of IDs
        Classes: Array.isArray(child.Classes) 
          ? child.Classes.map(cls => (typeof cls === 'object' ? cls.id : cls)) 
          : [], 
      })),
      contacts: emergencyContacts.map(contact => ({
        ...contact,
        SignedUP: contact.SignedUp || false 
      }))
    };

    try {
      // Submit form to backend
      await axios.post(`${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission/submit/`, payload);
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      // Handle validation errors from backend
      if (err.response && err.response.status === 400) {
        const serverErrors = err.response.data.errors;
        const sP1Errs = {};
        const sP2Errs = {};
        
        // Separate parent errors
        Object.keys(serverErrors).forEach(key => {
          if (key.startsWith('p1_')) sP1Errs[key] = serverErrors[key][0];
          if (key.startsWith('p2_')) sP2Errs[key] = serverErrors[key][0];
        });
        setP1Errors(sP1Errs);
        setP2Errors(sP2Errs);

        // Handle children errors (can be array of strings or array of objects)
        if (serverErrors.children) {
          if (Array.isArray(serverErrors.children) && serverErrors.children.length > 0) {
            // If first element is a string, it's a general error message
            if (typeof serverErrors.children[0] === 'string') {
              setChildGeneralError(serverErrors.children[0]);
            } else {
              // Otherwise, it's field-specific errors for each child
              setChildrenErrors(serverErrors.children);
            }
          } else {
            setChildGeneralError(serverErrors.children[0] || serverErrors.children);
          }
        }
        
        setError(isAr ? "يرجى تصحيح الأخطاء الموضحة أدناه." : "Please fix the validation errors.");
        scrollToFirstError();
      } else {
        setError(isAr ? "فشل في إرسال البيانات." : "Failed to submit data.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Convert metadata lists to select options format
  const getOptions = (list, useCode = false) => 
    list.map(item => ({
      label: isAr ? (item.Name_ar || item.name || item.Name) : (item.Name_en || item.name || item.Name),
      value: useCode ? (item.code || item.id) : item.id
    }));

  // Toggle parent section expansion
  const toggleParent = (num) => {
    if (num === 1) setParent1({ ...parent1, isExpanded: !parent1.isExpanded });
    else setParent2({ ...parent2, isExpanded: !parent2.isExpanded });
  };

  // Toggle child section expansion
  const toggleChild = (index) => {
    const newChildren = [...children];
    newChildren[index].isExpanded = !newChildren[index].isExpanded;
    setChildren(newChildren);
  };

  // Add a new child to the form
  const addNewChild = () => {
    setChildren([...children, { 
      id: Date.now(), 
      isExpanded: true,
      FirstName: "", 
      LastName: "", 
      BirthDate: "", 
    //  Nationality: "", 
     // countries: "", 
      Gender: "",
      Notes: "", 
      Medications: "", 
      DoctorName: "", 
      DoctorPhone: "", 
      Allergies: "",
      AppartmentHouse: "", 
      ZipCode: "", 
      City: "", 
      State: "", 
      Street: "",
      Classes: []
    }]);
  };

  // Remove a child from the form (minimum 1 child required)
  const removeChild = (id) => {
    if (children.length > 1) {
      setChildren(children.filter(child => child.id !== id));
    }
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <LoadingDots/>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, direction: isAr ? "rtl" : "ltr", position: "relative" }}>
      
      {/* Language Switcher - Fixed position at top right/left, hide on 404 page */}
      {!notFound && (
        <Box sx={{ position: "absolute", top: 20, right: isAr ? "auto" : 20, left: isAr ? 20 : "auto", zIndex: 1000 }}>
          <LanguageSwitcher />
        </Box>
      )}

      {/* Header Section - Always visible unless 404 */}
      {!notFound && (
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: "bold", color: "#5e35b1" }}>
            {isAr ? "مرحباً بكم في" : "Welcome to"}
          </Typography>
          <Typography variant="h4" sx={{ color: "#7b1fa2", fontWeight: "500", mb: 2 }}>
            {data?.nursery_name || "Our Nursery"}
          </Typography>

      {/* Form Title */}
          {data?.title && (
            <Box 
              sx={{ 
                display: "inline-block",
                backgroundColor: "rgba(123, 31, 162, 0.08)", 
                color: "#7b1fa2",
                padding: "8px 24px",
                borderRadius: "50px", 
                border: "1px solid rgba(123, 31, 162, 0.2)",
                fontSize: "1.1rem",
                fontWeight: "500",
                mb: 3,
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
              }}
            >
              {data.title}
            </Box>
          )}

          {/* Show description only if link is valid */}
          {data?.valid && (
            <Typography variant="body1" sx={{ color: "#666", maxWidth: "600px", mx: "auto", mt: 2 }}>
              {isAr 
                ? "يرجى ملء نموذج التقديم أدناه بالمعلومات الصحيحة. سنقوم بمراجعة طلبكم والتواصل معكم في أقرب وقت ممكن."
                : "Please fill out the application form below with accurate information. We will review your application and contact you as soon as possible."
              }
            </Typography>
          )}
        </Box>
      )}

      {notFound ? (
        <Paper elevation={4} sx={{ p: { xs: 4, md: 6 }, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="h1" sx={{ fontSize: "120px", fontWeight: "bold", color: "#5e35b1", mb: 2 }}>
            404
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#666", mb: 2 }}>
            {"Page Not Found"}
          </Typography>
          <Typography variant="body1" sx={{ color: "#999" }}>
            { "Sorry, the link you're looking for doesn't exist or has been removed."
            }
          </Typography>
        </Paper>
      ) : !data?.valid ? (
        /* Invalid Link - Show error from validation endpoint, hide form */
        <Paper elevation={4} sx={{ p: { xs: 2, md: 5 }, borderRadius: 3, textAlign: "center" }}>
         <Alert 
            severity="error" 
            sx={{ 
                mb: 2, 
                direction: "ltr", 
                textAlign: "left" 
            }}
            >
            {(isAr ? error : error) || (isAr ? "رابط غير صالح أو منتهي الصلاحية" : "Invalid or expired link")}
         </Alert>
        </Paper>
      ) : success ? (
        /* Success Screen - Show after successful submission */
        <Paper elevation={4} sx={{ p: { xs: 4, md: 6 }, borderRadius: 3, textAlign: "center" }}>
          <Box sx={{ mb: 3 }}>
            {/* Success checkmark icon */}
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: "50%", 
                bgcolor: "#4caf50", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                mx: "auto", 
                mb: 3 
              }}
            >
              <Typography variant="h2" sx={{ color: "white" }}>✓</Typography>
            </Box>
            
            {/* Success message */}
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}>
              {isAr ? "تم إرسال الطلب بنجاح!" : "Application Submitted Successfully!"}
            </Typography>
            <Typography variant="body1" sx={{ color: "#666", maxWidth: "500px", mx: "auto" }}>
              {isAr 
                ? "شكراً لك على تقديم طلبك. سنقوم بمراجعته والتواصل معك قريباً."
                : "Thank you for submitting your application. We will review it and contact you soon."
              }
            </Typography>
          </Box>
        </Paper>
      ) : (
        /* Main Form - Show when link is valid and not yet submitted */
        <Paper elevation={4} sx={{ p: { xs: 2, md: 5 }, borderRadius: 3 }}>
          <Box>
            
            {/* Parents Section */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold", borderLeft: isAr ? 0 : "5px solid #5e35b1", borderRight: isAr ? "5px solid #5e35b1" : 0, px: 2 }}>
              {isAr ? "بيانات أولياء الأمور" : "Parents Information"}
            </Typography>

            {/* Render both parents */}
            {[1, 2].map((num) => {
              const p = num === 1 ? parent1 : parent2;
              const errs = num === 1 ? p1Errors : p2Errors;
              const setP = num === 1 ? setParent1 : setParent2;
              const prefix = `p${num}_`;
              const emailChecked = num === 1 ? p1EmailChecked : p2EmailChecked;
              const existsMessage = num === 1 ? p1ExistsMessage : p2ExistsMessage;
              const isOptional = num === 2; // Parent 2 is optional
              
              
              return (
                <Paper key={num} sx={{ mb: 2, overflow: "hidden", borderRadius: 3 }}>
                  {/* Parent header - clickable to expand/collapse */}
                  <Box 
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, cursor: "pointer", bgcolor: "#f3e5f5" }} 
                    onClick={() => toggleParent(num)}
                  >
                    <Typography variant="h6">
                      {num === 1 ? (isAr ? "بيانات ولي الأمر الأول" : "Parent 1 Details") 
                      : (isAr ? "بيانات ولي الأمر الثاني (اختياري)" : "Parent 2 Details  (Optional)")}
                    </Typography>
                    {p.isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                  
                  {/* Parent fields - collapsible */}
                  <Collapse in={p.isExpanded}>
                    <Box sx={{ p: 2 }}>
                      {/* Success message when parent exists */}
                      {existsMessage && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {existsMessage}
                        </Alert>
                      )}
                      
                    <PublicInfoCard 
                      title="" 
                      fields={[
                        { 
                          label: isAr ? "البريد الإلكتروني" : "Email", 
                          key: `${prefix}email`, 
                          value: p[`${prefix}email`], 
                          type: "email",
                          required: num === 1, // Only required for Parent 1
                          disabled: false, // Always editable
                          error: errs[`${prefix}email`],
                          onChange: (val) => handleEmailChange(val, num),
                        },
                        { 
                          label: isAr ? "الاسم الأول" : "First Name", 
                          key: `${prefix}first_name`, 
                          value: p[`${prefix}first_name`], 
                          required: num === 1, // Only required for Parent 1
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}first_name`] 
                        },
                        { 
                          label: isAr ? "اسم العائلة" : "Last Name", 
                          key: `${prefix}last_name`, 
                          value: p[`${prefix}last_name`], 
                          required: num === 1, // Only required for Parent 1
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}last_name`] 
                        },
                        { 
                          label: isAr ? "رقم الهاتف" : "Phone", 
                          key: `${prefix}phone`, 
                          value: p[`${prefix}phone`], 
                          required: num === 1 && !p.isExisting, // Required only for Parent 1 (new)
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}phone`] 
                        },
                        { 
                          label: isAr ? "العنوان" : "Address", 
                          key: `${prefix}address`, 
                          value: p[`${prefix}address`], 
                          required: num === 1 && !p.isExisting, // Required only for Parent 1 (new)
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}address`] 
                        },
                        { 
                          label: isAr ? "النوع" : "Gender", 
                          key: `${prefix}gender`, 
                          value: p[`${prefix}gender`], 
                          type: "select", 
                          options: getOptions(metadata.genders), 
                          required: num === 1 && !p.isExisting, // Required only for Parent 1 (new)
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}gender`] 
                        },
                        { 
                          label: isAr ? "الدولة" : "Country", 
                          key: `${prefix}country`, 
                          value: p[`${prefix}country`], 
                          type: "select", 
                          options: getOptions(metadata.countries, true), 
                          required: num === 1 && !p.isExisting, // Required only for Parent 1 (new)
                          disabled: !emailChecked || p.isExisting, 
                          error: errs[`${prefix}country`] 
                        },
                      ]}
                      onFieldChange={(key, val) => {
                        // Handle email changes separately
                        if (key === `${prefix}email`) {
                          handleEmailChange(val, num);
                        } else {
                          setP({ ...p, [key]: val });                        
                          if (num === 1) {
                            setP1Errors(prev => ({ ...prev, [key]: null }));
                          } else {
                            setP2Errors(prev => ({ ...prev, [key]: null }));
                          }
                        }
                      }}
                    />
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}

            {/* Children Section */}
            <Typography variant="h5" sx={{ mb: 3, mt: 5, fontWeight: "bold", borderLeft: isAr ? 0 : "5px solid #5e35b1", borderRight: isAr ? "5px solid #5e35b1" : 0, px: 2 }}>
              {isAr ? "بيانات الأطفال" : "Children Information"}
            </Typography>

            {/* Render all children */}
            {children.map((child, index) => {
              const childErr = childrenErrors[index] || {};
              return (
                <Paper key={child.id} sx={{ mb: 3, overflow: "hidden", borderRadius: 3, border: "1px solid #e0e0e0" }}>
                  {/* Child header with expand/collapse and delete button */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "#e8eaf6" }}>
                    <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexGrow: 1 }} onClick={() => toggleChild(index)}>
                      {child.isExpanded ? <ExpandLess /> : <ExpandMore />}
                      <Typography variant="h6" sx={{ ml: 1, mr: 1, color: "#1976d2" }}>
                        {child.FirstName ? `${child.FirstName} ${child.LastName}` : (isAr ? `الطفل #${index + 1}` : `Child #${index + 1}`)}
                      </Typography>
                    </Box>
                    {/* Delete button - only show if more than 1 child */}
                    {children.length > 1 && (
                      <IconButton color="error" onClick={(e) => { e.stopPropagation(); removeChild(child.id); }}>
                        <DeleteOutline />
                      </IconButton>
                    )}
                  </Box>

                  {/* Child fields - collapsible */}
                  <Collapse in={child.isExpanded}>
                    <Box sx={{ p: 3 }}>
                      {/* Personal Information */}
                      <PublicInfoCard 
                        title={isAr ? "المعلومات الشخصية" : "Personal Info"}
                        fields={[
                          { label: isAr ? "الاسم الأول" : "First Name", key: "FirstName", value: child.FirstName, required: true, error: childErr.FirstName ? childErr.FirstName[0] : null },
                          { label: isAr ? "اسم العائلة" : "Last Name", key: "LastName", value: child.LastName, required: true, error: childErr.LastName ? childErr.LastName[0] : null },
                          { label: isAr ? "تاريخ الميلاد" : "Birth Date", key: "BirthDate", value: child.BirthDate, type: "date", required: true, error: childErr.BirthDate ? childErr.BirthDate[0] : null },
                          { label: isAr ? "النوع" : "Gender", key: "Gender", value: child.Gender, type: "select", options: getOptions(metadata.genders), required: true, error: childErr.Gender ? childErr.Gender[0] : null },
                        ]}
                       onFieldChange={(key, val) => {
                        const newChildren = [...children];
                        newChildren[index][key] = val;
                        setChildren(newChildren);
                        
                        if (childrenErrors[index] && childrenErrors[index][key]) {
                          const newChildrenErrors = [...childrenErrors];
                          const updatedChildErrors = { ...newChildrenErrors[index] };
                          delete updatedChildErrors[key]; 
                          newChildrenErrors[index] = updatedChildErrors;
                          setChildrenErrors(newChildrenErrors);
                        }
                        }}
                      />

                      {/* Class Selection */}
                      <PublicRoomsCard 
                        title={isAr ? "الفصل الدراسي" : "Class Selection"}
                        allRooms={metadata.rooms.map(r => ({ id: r.id, ClassName: r.name }))}
                        selectedRooms={child.Classes || []} 
                        onSelectionChange={(selectedList) => {
                        const newChildren = [...children];
                        newChildren[index].Classes = selectedList; 
                        setChildren(newChildren);

                        if (selectedList.length > 0) {
                          const newChildrenErrors = [...childrenErrors];
                          if (newChildrenErrors[index]) {
                            const updatedChildErrors = { ...newChildrenErrors[index] };
                            delete updatedChildErrors.Classes;
                            newChildrenErrors[index] = updatedChildErrors;
                            setChildrenErrors(newChildrenErrors);
                          }
                        }
                        }}
                      />
                      {/* Show class selection error if exists */}
                      {childErr.Classes && (
                        <Typography variant="caption" color="error" sx={{ px: 2, display: "block", mt: 1 }}>
                          {childErr.Classes[0]}
                        </Typography>
                      )}

                      {/* Medical Information */}
                      <PublicInfoCard 
                        title={isAr ? "المعلومات الطبية والملاحظات" : "Medical Info & Notes"}
                        fields={[
                          { label: isAr ? "الحساسية" : "Allergies", key: "Allergies", value: child.Allergies, error: childErr.Allergies ? childErr.Allergies[0] : null },
                          { label: isAr ? "الأدوية" : "Medications", key: "Medications", value: child.Medications, error: childErr.Medications ? childErr.Medications[0] : null },
                          { label: isAr ? "اسم الطبيب" : "Doctor Name", key: "DoctorName", value: child.DoctorName, error: childErr.DoctorName ? childErr.DoctorName[0] : null },
                          { label: isAr ? "هاتف الطبيب" : "Doctor Phone", key: "DoctorPhone", value: child.DoctorPhone, error: childErr.DoctorPhone ? childErr.DoctorPhone[0] : null },
                          { label: isAr ? "ملاحظات إضافية" : "Additional Notes", key: "Notes", value: child.Notes, error: childErr.Notes ? childErr.Notes[0] : null },
                        ]}
                        onFieldChange={(key, val) => {
                          const newChildren = [...children];
                          newChildren[index][key] = val;
                          setChildren(newChildren);

                          // Clear error for this field if it exists
                          if (childrenErrors[index] && childrenErrors[index][key]) {
                            const newChildrenErrors = [...childrenErrors];
                            const updatedChildErrors = { ...newChildrenErrors[index] };
                            delete updatedChildErrors[key]; 
                            newChildrenErrors[index] = updatedChildErrors;
                            setChildrenErrors(newChildrenErrors);
                          }
                        }}
                      />
                      
                      {/* Residential Address */}
                      {/* <PublicInfoCard 
                        title={isAr ? "عنوان السكن" : "Residential Address"}
                        fields={[
                          { label: isAr ? "الشارع" : "Street", key: "Street", value: child.Street },
                          { label: isAr ? "المدينة" : "City", key: "City", value: child.City },
                          { label: isAr ? "الولاية" : "State", key: "State", value: child.State },
                          { label: isAr ? "الرمز البريدي" : "Zip Code", key: "ZipCode", value: child.ZipCode },
                          { label: isAr ? "رقم الشقة/المنزل" : "Apartment/House", key: "AppartmentHouse", value: child.AppartmentHouse },
                        ]}
                        onFieldChange={(key, val) => {
                          const newChildren = [...children];
                          newChildren[index][key] = val;
                          setChildren(newChildren);
                        }}
                      /> */}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}

            {/* Add Another Child Button */}
            <Button variant="outlined" color="primary" onClick={addNewChild} sx={{ mb: 2 }}>
              {isAr ? "+ إضافة طفل آخر" : "+ Add Another Child"}
            </Button>

            {/* Display general child error (like room availability) */}
            {childGeneralError && (
              <Alert severity="error" sx={{ mb: 4, mt: 1, textAlign: isAr ? "right" : "left" }}>
                {childGeneralError}
              </Alert>
            )}

            <Divider sx={{ my: 4, borderColor: "rgba(110, 83, 125, 1)" }} />

            {/* Emergency Contacts Section */}
            <PublicContactsSection 
              title={isAr ? "جهات اتصال للطوارئ" : "Emergency Contacts"}
              contacts={emergencyContacts}
              onContactsChange={setEmergencyContacts}
              isAr={isAr}
            />

            {/* Submit Button and Error Display */}
            <Box sx={{ mt: 6, textAlign: "center", pb: 5 }}>
              
              {/* Display general error message */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, textAlign: isAr ? "right" : "left" }}>
                  {error}
                </Alert>
              )}

              {/* Submit Button */}
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSubmit} 
                disabled={submitting}
                sx={{ px: 10, py: 2, borderRadius: 10, bgcolor: "#5e35b1", "&:hover": { bgcolor: "#4527a0" } }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : (isAr ? "إرسال الطلب" : "Submit Application")}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default AdmissionFormPublic;